import { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import { CostCalculator } from "../cost/CostCalculator.js";
import { loadOpenRouterRoutingEnv } from "../sync/openRouterRoutingEnv.js";
import type { OpenRouterRoutingConfig } from "../sync/openRouterRoutingEnv.js";

export type AiToolsInvokeClient = {
  catalog: AiModelsCatalogClient;
  calculator: CostCalculator;
  routingEnv: OpenRouterRoutingConfig;
};

export type CreateAiToolsInvokeClientOptions = {
  cacheTtlMs?: number;
  bundledOnly?: boolean;
  costIncludeBreakdown?: boolean;
  cacheKey?: string;
};

function clientConfigKey(options: CreateAiToolsInvokeClientOptions = {}): string {
  return JSON.stringify({
    cacheTtlMs: options.cacheTtlMs ?? null,
    bundledOnly: options.bundledOnly ?? false,
    costIncludeBreakdown: options.costIncludeBreakdown ?? false,
    cacheKey: options.cacheKey ?? "default",
  });
}

export function createAiToolsInvokeClient(
  options: CreateAiToolsInvokeClientOptions = {},
): AiToolsInvokeClient {
  const cacheKey = options.cacheKey ?? "default";
  const catalog = new AiModelsCatalogClient({
    cacheKey,
    cacheTtlMs: options.cacheTtlMs,
    bundledOnly: options.bundledOnly,
    resolverOptions: {
      routingEnv: loadOpenRouterRoutingEnv(),
    },
  });
  const calculator = new CostCalculator(catalog, {
    includeBreakdown: options.costIncludeBreakdown,
  });
  return {
    catalog,
    calculator,
    routingEnv: loadOpenRouterRoutingEnv(),
  };
}

let singleton: AiToolsInvokeClient | null = null;
let singletonKey: string | null = null;

/** Reset process singleton (tests). */
export function resetAiToolsInvokeClientForTests(): void {
  singleton = null;
  singletonKey = null;
}

/**
 * Process singleton with config-key invalidation.
 * Returns null when catalog bootstrap fails.
 */
export async function getAiToolsInvokeClient(
  options: CreateAiToolsInvokeClientOptions = {},
  logger?: { warn?: (msg: string, err?: unknown) => void },
): Promise<AiToolsInvokeClient | null> {
  const key = clientConfigKey(options);
  if (singleton && singletonKey === key) return singleton;

  try {
    const client = createAiToolsInvokeClient(options);
    await client.catalog.getAllModels();
    singleton = client;
    singletonKey = key;
    return client;
  } catch (err) {
    logger?.warn?.("Failed to bootstrap ai-tools invoke client", err);
    return null;
  }
}
